import { Button, DataTableColumnHeader, Text } from "@/components/ui";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import {
  getLienId,
  type LienViewModel,
} from "@/hooks/matter-view-models/use-liens-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { type Connection } from "@/lib/types";
import { getConnectionContactName } from "@/lib/utils/connections/item-contact-connections";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { formatDate } from "@/lib/utils/format";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { DollarSign } from "lucide-react";

// Component for the trust check button
const TrustCheckButton = ({
  matterId,
  lienId,
}: {
  matterId: string;
  lienId: string;
}) => {
  const { openTrustCheckModal } = useGlobalModal();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    openTrustCheckModal({
      matterId,
      trustCheckType: "Withdraw",
      editing: false,
      lienId,
    });
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleClick}
      className="h-8 w-8"
    >
      <DollarSign className="h-4 w-4" />
    </Button>
  );
};

const renderCurrencyOrDash = (value: number | null | undefined) =>
  value == null ? "-" : <CurrencyWrapper value={value} />;

const columnHelper = createColumnHelper<
  LienViewModel & {
    matterLienHolder?: Connection;
    matterLienRepresentative?: Connection;
  }
>();

export const matterLiensColumns: ColumnDef<
  LienViewModel & {
    matterLienHolder?: Connection;
    matterLienRepresentative?: Connection;
  }
>[] = [
  columnHelper.accessor("dateCreated", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    sortingFn: "customDateTime",
    id: "Date Created",
    cell: (cell) => {
      const dateCreated = cell.getValue();
      return (
        <Text variant="subHeading">
          {dateCreated
            ? formatDate(convertToLocalFromDatabaseDateTime(dateCreated))
            : "-"}
        </Text>
      );
    },
  }),
  columnHelper.accessor((row) => row.label, {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Label",
    enableHiding: false,
    cell: ({ cell }) => (
      <Text variant="body" className="font-semibold">
        {cell.getValue()}
      </Text>
    ),
  }),
  columnHelper.accessor((row) => row.category, {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Category",
    cell: ({ cell }) => (
      <Text variant="subHeading">{cell.getValue() ?? "-"}</Text>
    ),
  }),
  columnHelper.accessor((row) => row.subCategory, {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Subcategory",
    cell: ({ cell }) => (
      <Text variant="subHeading">{cell.getValue() ?? "-"}</Text>
    ),
  }),
  columnHelper.accessor((row) => row.originalAmount, {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Original Amount",
    cell: ({ cell }) => (
      <Text variant="subHeading">{renderCurrencyOrDash(cell.getValue())}</Text>
    ),
  }),
  columnHelper.accessor((row) => row.negotiatedAmount, {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Negotiated Amount",
    cell: ({ cell }) => (
      <Text variant="subHeading">{renderCurrencyOrDash(cell.getValue())}</Text>
    ),
  }),
  columnHelper.accessor((row) => row.paidAmount, {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Paid Amount",
    cell: ({ cell }) => (
      <Text variant="subHeading">{renderCurrencyOrDash(cell.getValue())}</Text>
    ),
  }),
  columnHelper.accessor(
    (row) => {
      const amountToPay = row.negotiatedAmount ?? row.originalAmount;
      if (amountToPay == null) {
        return 0;
      }
      return amountToPay - (row.paidAmount ?? 0);
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Balance Remaining",
      cell: ({ row }) => {
        const amountToPay =
          row.original.negotiatedAmount ?? row.original.originalAmount;
        if (amountToPay == null) {
          return <Text variant="subHeading">-</Text>;
        }
        const balanceRemaining = amountToPay - (row.original.paidAmount ?? 0);
        return (
          <Text variant="subHeading">
            <CurrencyWrapper value={balanceRemaining} />
          </Text>
        );
      },
    },
  ),
  columnHelper.accessor("matterLienHolder", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Lien Holder",
    cell: ({ cell }) => (
      <Text variant="subHeading">
        {cell.getValue()
          ? getConnectionContactName(cell.getValue()?.contact)
          : "-"}
      </Text>
    ),
  }),
  columnHelper.accessor("matterLienRepresentative", {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Lien Representative",
    cell: ({ cell }) => (
      <Text variant="subHeading">
        {cell.getValue()
          ? getConnectionContactName(cell.getValue()?.contact)
          : "-"}
      </Text>
    ),
  }),
  columnHelper.accessor((row) => row.status, {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Status",
    cell: ({ cell }) => (
      <Text variant="subHeading">{cell.getValue() ?? "-"}</Text>
    ),
  }),
  columnHelper.accessor((row) => row.description, {
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    id: "Description",
    cell: ({ cell }) => (
      <Text variant="subHeading" className="line-clamp-4 max-w-[200px]">
        {cell.getValue() ?? "-"}
      </Text>
    ),
  }),
  columnHelper.accessor("trustChecks", {
    id: "Trust Checks",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    cell: ({ row }) => {
      const trustChecks = (row.original as LienViewModel).trustChecks;
      return <Text variant="subHeading">{trustChecks.length}</Text>;
    },
  }),
  columnHelper.accessor("matterId", {
    id: "Actions",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    cell: ({ row }) => {
      const matterId = (row.original as LienViewModel).matterId;
      const lienId = getLienId(row.original);
      return matterId ? (
        <TrustCheckButton matterId={matterId} lienId={lienId} />
      ) : null;
    },
  }),
] as ColumnDef<
  LienViewModel & {
    matterLienHolder?: Connection;
    matterLienRepresentative?: Connection;
  }
>[];

// Function to create columns with additional fields when on settlements page
export const createMatterLiensColumns = (
  isOnSettlementsPage: boolean = false,
): ColumnDef<
  LienViewModel & {
    matterLienHolder?: Connection;
    matterLienRepresentative?: Connection;
  }
>[] => {
  if (!isOnSettlementsPage) {
    return matterLiensColumns;
  }

  // Add goal amount, negotiated amount, and reduction fee agreement columns to the end
  const columns = [...matterLiensColumns];

  const goalAmountColumn: ColumnDef<
    LienViewModel & {
      matterLienHolder?: Connection;
      matterLienRepresentative?: Connection;
    }
  > = {
    id: "Goal Amount",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    accessorFn: (row) => row.goalAmount,
    cell: ({ row }) => {
      const value = row.original.goalAmount;
      return <Text variant="subHeading">{renderCurrencyOrDash(value)}</Text>;
    },
  };

  const reductionFeeAgreementColumn: ColumnDef<
    LienViewModel & {
      matterLienHolder?: Connection;
      matterLienRepresentative?: Connection;
    }
  > = {
    id: "Reduction Fee Agreement",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    accessorFn: (row) => row.reductionFeeAgreement,
    cell: ({ row }) => {
      const value = row.original.reductionFeeAgreement;
      return <Text variant="subHeading">{value ? "Yes" : "No"}</Text>;
    },
  };

  columns.push(goalAmountColumn, reductionFeeAgreementColumn);

  return columns;
};
